import { useQuery } from '@tanstack/react-query';
import { container } from '@src/container';
import { UniversityService } from '@src/application/universities/usecases';

/**
 * Hook: useGetUniversityById
 * Fetches a university by its ID
 *
 * @example
 * const { data: university, isLoading, error } = useGetUniversityById('university-id');
 */
export function useGetUniversityById(id: string | undefined) {
    const universityService = container.resolve<UniversityService>('UniversityService');

    return useQuery({
        queryKey: ['universities', 'by-id', id],
        queryFn: async () => {
            if (!id) return null;

            try {
                const result = await universityService.getUniversityById(id);
                if (import.meta.env.DEV) {
                    console.log('✅ University fetched:', result);
                }
                return result;
            } catch (error) {
                if (import.meta.env.DEV) {
                    console.error('❌ Failed to fetch university:', error);
                }
                throw error;
            }
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 10, // 10 minutes
        gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });
}